import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useHandleNavigate } from './useHandleNavigate';

/**
 * Syncs URL (pathname) with app state when user is authenticated.
 * Listens to popstate and runs initial navigation on mount/projects change.
 */
export function useUrlSync() {
  const { isAuthenticated } = useAuth();
  const { setCurrentScreen, setIsAnalyzing } = useNavigation();
  const { handleNavigate, projects, setSelectedProject } = useHandleNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;

    const runNavigation = async () => {
      const path = window.location.pathname;

      if (path === '/register') {
        window.history.replaceState({}, '', '/login');
        setCurrentScreen('login');
        return;
      }
      if (path === '/login') {
        setCurrentScreen('login');
        return;
      }
      if (path === '/welcome') {
        window.history.replaceState({}, '', '/home');
        setCurrentScreen('home');
        return;
      }
      if (path === '/home') {
        setCurrentScreen('home');
        setIsAnalyzing(false);
        return;
      }
      if (path === '/settings') {
        setCurrentScreen('settings');
        return;
      }
      if (path.startsWith('/test/')) {
        const parts = path.split('/');
        if (parts.length === 3) {
          const testId = parts[2];
          setCurrentScreen('test');
          await handleNavigate('test', testId);
        } else if (parts.length === 4) {
          const projectId = parts[2];
          const testId = parts[3];
          const project = projects.find(p => p.id === projectId);
          if (project) {
            setSelectedProject(project);
            setCurrentScreen('test');
            await handleNavigate('test', testId, projectId);
          } else if (projects.length > 0) {
            const found = projects.find(p => p.id === projectId);
            if (found) {
              setSelectedProject(found);
              setCurrentScreen('test');
              await handleNavigate('test', testId, projectId);
            } else {
              window.history.pushState({}, '', '/home');
              setCurrentScreen('home');
            }
          }
        }
        return;
      }
      if (path.startsWith('/analysis/')) {
        const parts = path.split('/');
        const projectId = parts[2];
        const project = projects.find(p => p.id === projectId);
        if (project) {
          setSelectedProject(project);
          setIsAnalyzing(true);
          setCurrentScreen('home');
        } else {
          const found = projects.find(p => p.id === projectId);
          if (found) {
            setSelectedProject(found);
            setIsAnalyzing(true);
            setCurrentScreen('home');
          } else if (projects.length > 0) {
            window.history.pushState({}, '', '/home');
            setCurrentScreen('home');
          }
        }
        return;
      }
      if (path === '/') {
        window.history.pushState({}, '', '/home');
        setCurrentScreen('home');
      }
    };

    window.addEventListener('popstate', runNavigation);
    runNavigation();
    return () => window.removeEventListener('popstate', runNavigation);
  }, [
    isAuthenticated,
    projects,
    setCurrentScreen,
    setIsAnalyzing,
    handleNavigate,
    setSelectedProject,
  ]);
}
